// „Замяна с взаимозаменяеми продукти" при разнасяне: за всеки продукт с
// недостиг — името и на ВТОРИ ред „Липсват Y · склад · налични X";
// заместителите като отметки на ЕДИН ред „☐ име · налични X" (34px като
// редовете на справките, 44px с пръст), няколко наведнъж, с количество в
// мярката на ПОЛУЧАТЕЛЯ и лента „Покрито X / Y". Space/Enter върху заместител
// го отмята и фокусира количеството му; Enter в количеството го потвърждава,
// Esc връща старото.
//
// Количеството е текстово поле с inputMode=decimal и ЗАПЕТАЯ (както всички
// количества в модула) — не type=number: там ↑/↓ стъпват с ±1 и десетичният
// знак е точка.
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatQty } from "../../components/qty";
import { parseDecimal } from "../../components/numbers";
import { CheckBox } from "../../components/filters/CheckBox";
import { TruncatedText } from "../../components/list/TruncatedText";
import { useInventoryShell } from "../../components/shell/InventoryShellContext";
import { QTY_EPSILON } from "./reportPostingShared";
import type { DonorChoice, SubstitutionIssue } from "./reportPostingSubstitutionLogic";

type Props = {
  issues: SubstitutionIssue[];
  warehouseNameOf: (productId: number, warehouseId: number) => string;
  decimalsOf: (productId: number) => number;
  onChange: (
    warehouseId: number,
    recipientProductId: number,
    donorProductId: number,
    quantity: number | null,
  ) => void;
};

export const ReportPostingSubstitutions = ({
  issues,
  warehouseNameOf,
  decimalsOf,
  onChange,
}: Props) => {
  const { t } = useTranslation();
  const { isNarrow } = useInventoryShell();
  if (!issues.length) return null;
  return (
    <section className="flex flex-col gap-0.5 rounded-xl border border-shade-2 bg-panel px-3.5 py-3">
      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1 pb-1.5">
        <span className="text-[15px] font-semibold leading-[normal] text-ink">
          {t("inventoryPage.postingSubsTitle")}
        </span>
        <span className="text-[13px] leading-[normal] text-muted">
          {t("inventoryPage.postingSubsHint")}
        </span>
      </div>
      {issues.map((issue) => {
        const decimals = decimalsOf(issue.recipientId);
        const done = issue.covered >= issue.missing - QTY_EPSILON;
        const percent = Math.min(100, (issue.covered / issue.missing) * 100);
        const unitSuffix = issue.unit ? ` ${issue.unit}` : "";
        const missingText = t("inventoryPage.postingMissing", {
          qty: `${formatQty(issue.missing, decimals)}${unitSuffix}`,
        });
        const stockText = `${warehouseNameOf(issue.recipientId, issue.warehouseId)} · ${t(
          "inventoryPage.postingAvailableShort",
        )} ${formatQty(issue.available, decimals)}${unitSuffix}`;
        return (
          <div
            key={issue.recipientId}
            className={`grid items-center border-t border-shade-1 py-1.5 ${
              isNarrow
                ? "grid-cols-1 gap-2.5"
                : "grid-cols-[minmax(170px,250px)_minmax(0,1fr)_190px] gap-[18px]"
            }`}
          >
            <div className="flex min-w-0 flex-col gap-0.5">
              <TruncatedText
                text={issue.recipientName}
                className="text-[13.5px] font-semibold leading-5 text-ink"
              />
              {/* „Липсват" е първо — при отрязване се губи складът, не липсата */}
              <TruncatedText
                text={`${missingText} · ${stockText}`}
                className="text-[12.5px] leading-[18px] text-muted"
              >
                <span className="font-medium text-danger-ink">{missingText}</span> · {stockText}
              </TruncatedText>
            </div>
            <div className="flex flex-wrap gap-2">
              {issue.donors.map((donor) => (
                <DonorToggle
                  key={donor.donorId}
                  donor={donor}
                  decimals={decimals}
                  unit={issue.unit}
                  donorDecimals={decimalsOf(donor.donorId)}
                  onChange={(quantity) =>
                    onChange(issue.warehouseId, issue.recipientId, donor.donorId, quantity)
                  }
                />
              ))}
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="flex justify-between text-[12.5px] leading-[normal] text-body">
                <span>{t("inventoryPage.postingCoveredLabel")}</span>
                <span
                  className={`font-sheet font-semibold ${
                    done
                      ? "text-ok-deep"
                      : issue.covered > QTY_EPSILON
                        ? "text-brand-deep-ink"
                        : "text-body"
                  }`}
                >
                  {formatQty(issue.covered, decimals)} / {formatQty(issue.missing, decimals)}
                </span>
              </span>
              <span className="flex h-1.5 overflow-hidden rounded-[3px] bg-fill-2">
                {/* ширината е стойност от данните — единственият inline стил тук */}
                <span
                  className={`rounded-[3px] transition-[width] duration-200 ${
                    done ? "bg-ok-solid" : "bg-brand"
                  }`}
                  style={{ width: `${percent}%` }}
                />
              </span>
            </div>
          </div>
        );
      })}
    </section>
  );
};

// Един заместител: отметка · име · наличното; отметнат — поле за количество.
function DonorToggle({
  donor,
  decimals,
  unit,
  donorDecimals,
  onChange,
}: {
  donor: DonorChoice;
  decimals: number;
  unit: string | null;
  donorDecimals: number;
  onChange: (quantity: number | null) => void;
}) {
  const { t } = useTranslation();
  const { isNarrow } = useInventoryShell();
  const on = donor.chosen != null;
  const disabled = !on && donor.capRecipient <= QTY_EPSILON;
  const [draft, setDraft] = useState<string>("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const focusOnOpenRef = useRef(false);
  // Esc връща старото количество — blur-ът след него не бива да записва
  const escapingRef = useRef(false);

  // Полето показва избраното количество; при външна промяна се пренаписва
  useEffect(() => {
    setDraft(on ? formatQty(donor.chosen ?? 0, decimals) : "");
  }, [on, donor.chosen, decimals]);

  useEffect(() => {
    if (!on || !focusOnOpenRef.current) return;
    focusOnOpenRef.current = false;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [on]);

  const commit = () => {
    const value = parseDecimal(draft);
    const factor = 10 ** decimals;
    const clamped = Math.max(0, Math.min(value, donor.capRecipient));
    const rounded = Math.round(clamped * factor) / factor;
    onChange(rounded > QTY_EPSILON ? rounded : null);
  };

  return (
    <div
      className={`flex items-center overflow-hidden rounded-[9px] border ${
        on ? "border-ok-solid/45 bg-ok-tint" : "border-shade-3 bg-surface"
      } ${disabled ? "opacity-55" : ""}`}
    >
      <button
        type="button"
        role="checkbox"
        aria-checked={on}
        disabled={disabled}
        onClick={() => {
          if (on) {
            onChange(null);
            return;
          }
          focusOnOpenRef.current = true;
          onChange(donor.capRecipient);
        }}
        className="flex min-h-[32px] cursor-pointer items-center gap-2 border-none bg-transparent px-2.5 py-1 text-left text-ink outline-none focus-visible:shadow-[inset_0_0_0_2px_var(--color-brand)] disabled:cursor-not-allowed pointer-coarse:min-h-[42px]"
      >
        <CheckBox checked={on} tone="ok" />
        <span className="whitespace-nowrap text-[13px] font-medium leading-5">
          {donor.name}
          <span className="text-xs font-normal text-muted">
            {" · "}
            {t("inventoryPage.postingAvailableShort")}{" "}
            {formatQty(donor.availableDonor, donorDecimals)}
            {donor.donorUnit ? ` ${donor.donorUnit}` : ""}
          </span>
        </span>
      </button>
      {on && (
        <div className="flex items-center gap-[5px] pl-1 pr-2.5">
          <input
            ref={inputRef}
            value={draft}
            inputMode="decimal"
            enterKeyHint="done"
            aria-label={t("inventoryPage.postingSubQty", { name: donor.name })}
            onChange={(event) => setDraft(event.target.value.replace(/[^0-9.,]/g, ""))}
            onFocus={(event) => event.target.select()}
            onBlur={() => {
              if (escapingRef.current) {
                escapingRef.current = false;
                return;
              }
              commit();
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                commit();
              } else if (event.key === "Escape") {
                event.preventDefault();
                escapingRef.current = true;
                setDraft(formatQty(donor.chosen ?? 0, decimals));
                inputRef.current?.blur();
              }
            }}
            className={`w-16 rounded-md border border-ok-solid/45 bg-surface px-1.5 py-0.5 text-right font-sheet text-ink outline-none focus:border-ok-solid ${
              isNarrow ? "text-base" : "text-[13px]"
            }`}
          />
          <span className="text-xs text-body">{unit}</span>
        </div>
      )}
    </div>
  );
}

export default ReportPostingSubstitutions;
